import { useState } from "react";
import BaseModal from "../BaseComponents/BaseModal/BaseModal";
import styles from "./AddCredentialsSetModal.module.css";
import { useCreateCredential } from "../../api/suppliers/useCredentialsApi";

const ENVIRONMENTS = ["Production", "Test", "Staging", "Development"];

const AUTH_TYPES = [
  "API Key",
  "Basic Auth",
  "OAuth2",
  "Bearer Token",
  "Custom"
];

  const emptyForm = {
  credentialId: "",
  supplier: "",
  environment: "",
  status: "active",
  apiEndpoint: "",
  authType: "",
  apiKey: "",
  apiSecret: "",
  username: "",
  password: "",
  clientId: "",
  clientSecret: "",
  bearerToken: "",
  customAuth: ""
};

const ENVIRONMENTS_MAP = { Staging: "Sandbox" };

const AddCredentialSetModal = ({ open, onClose, onSubmit, suppliers = [] }) => {
  const createCredential = useCreateCredential();
  const [form, setForm] = useState(emptyForm);
  const supplierOptions = suppliers.map((s) => ({
    id: s.id,
    name: s.name || s.supplierName || "",
  }));
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showApiKey, setShowApiKey] = useState(false);
  const [showApiSecret, setShowApiSecret] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showClientSecret, setShowClientSecret] = useState(false);
  const [showBearerToken, setShowBearerToken] = useState(false);

  const handleChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    // Clear error when user starts typing
    if (errors[key]) {
      setErrors(prev => ({ ...prev, [key]: "" }));
    }
  };

  const handleBlur = (key) => {
    setTouched(prev => ({ ...prev, [key]: true }));
    validateField(key, form[key]);
  };

  const validateField = (key, value) => {
    let error = "";

    switch (key) {
      case "credentialId":
        if (!value || !value.trim()) {
          error = "Credential Set ID is required";
        } else if (value.length < 4) {
          error = "Credential ID must be at least 4 characters";
        }
        break;
      case "supplier":
        if (!value) {
          error = "Supplier is required";
        }
        break;
      case "environment":
        if (!value) {
          error = "Environment is required";
        }
        break;
      case "authType":
        if (!value) {
          error = "Authentication Type is required";
        }
        break;
      case "apiEndpoint":
        if (!value || !value.trim()) {
          error = "API Endpoint is required";
        } else if (!/^https?:\/\/.+/.test(value)) {
          error = "Please enter a valid URL (must start with http:// or https://)";
        }
        break;
      case "apiKey":
        if (form.authType === "API Key" && (!value || !value.trim())) {
          error = "API Key is required";
        }
        break;
      case "apiSecret":
        if (form.authType === "API Key" && (!value || !value.trim())) {
          error = "API Secret is required";
        }
        break;
      case "username":
        if (form.authType === "Basic Auth" && (!value || !value.trim())) {
          error = "Username is required";
        }
        break;
      case "password":
        if (form.authType === "Basic Auth" && (!value || !value.trim())) {
          error = "Password is required";
        }
        break;
      case "clientId":
        if (form.authType === "OAuth2" && (!value || !value.trim())) {
          error = "Client ID is required";
        }
        break;
      case "clientSecret":
        if (form.authType === "OAuth2" && (!value || !value.trim())) {
          error = "Client Secret is required";
        }
        break;
      case "bearerToken":
        if (form.authType === "Bearer Token" && (!value || !value.trim())) {
          error = "Bearer Token is required";
        }
        break;
      case "customAuth":
        if (form.authType === "Custom" && (!value || !value.trim())) {
          error = "Custom Authentication configuration is required";
        }
        break;
      default:
        break;
    }

    setErrors(prev => ({ ...prev, [key]: error }));
    return error;
  };

  const validateAllFields = () => {
    const newErrors = {};
    let isValid = true;

    if (!form.credentialId.trim()) {
      newErrors.credentialId = "Credential Set ID is required";
      isValid = false;
    } else if (form.credentialId.length < 4) {
      newErrors.credentialId = "Credential ID must be at least 4 characters";
      isValid = false;
    }

    if (!form.supplier) {
      newErrors.supplier = "Supplier is required";
      isValid = false;
    }

    if (!form.environment) {
      newErrors.environment = "Environment is required";
      isValid = false;
    }

    if (!form.authType) {
      newErrors.authType = "Authentication Type is required";
      isValid = false;
    }

    if (!form.apiEndpoint.trim()) {
      newErrors.apiEndpoint = "API Endpoint is required";
      isValid = false;
    } else if (!/^https?:\/\/.+/.test(form.apiEndpoint)) {
      newErrors.apiEndpoint = "Please enter a valid URL (must start with http:// or https://)";
      isValid = false;
    }

    // Auth-specific validation
    if (form.authType === "API Key") {
      if (!form.apiKey.trim()) {
        newErrors.apiKey = "API Key is required";
        isValid = false;
      }
      if (!form.apiSecret.trim()) {
        newErrors.apiSecret = "API Secret is required";
        isValid = false;
      }
    } else if (form.authType === "Basic Auth") {
      if (!form.username.trim()) {
        newErrors.username = "Username is required";
        isValid = false;
      }
      if (!form.password.trim()) {
        newErrors.password = "Password is required";
        isValid = false;
      }
    } else if (form.authType === "OAuth2") {
      if (!form.clientId.trim()) {
        newErrors.clientId = "Client ID is required";
        isValid = false;
      }
      if (!form.clientSecret.trim()) {
        newErrors.clientSecret = "Client Secret is required";
        isValid = false;
      }
    } else if (form.authType === "Bearer Token") {
      if (!form.bearerToken.trim()) {
        newErrors.bearerToken = "Bearer Token is required";
        isValid = false;
      }
    } else if (form.authType === "Custom") {
      if (!form.customAuth.trim()) {
        newErrors.customAuth = "Custom Authentication configuration is required";
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleReset = () => {
    setForm(emptyForm);
    setErrors({});
    setTouched({});
    setShowApiKey(false);
    setShowApiSecret(false);
    setShowPassword(false);
    setShowClientSecret(false);
    setShowBearerToken(false);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const buildApiPayload = () => {
    const env = ENVIRONMENTS_MAP[form.environment] || form.environment;
    const status = form.status === "active" ? "Active" : "Inactive";
    const base = {
      supplierId: Number(form.supplier),
      baseUrl: form.apiEndpoint.trim(),
      authenticationType: form.authType,
      environment: env || "Production",
      status,
    };
    if (form.authType === "API Key") {
      return { ...base, apiKey: form.apiKey, apiSecret: form.apiSecret };
    }
    if (form.authType === "Basic Auth") {
      return { ...base, username: form.username, password: form.password };
    }
    if (form.authType === "OAuth2") {
      return { ...base, apiKey: form.clientId, apiSecret: form.clientSecret };
    }
    if (form.authType === "Bearer Token") {
      return { ...base, accessToken: form.bearerToken };
    }
    if (form.authType === "Custom") {
      try {
        const parsed = typeof form.customAuth === "string" ? JSON.parse(form.customAuth) : form.customAuth;
        return { ...base, additionalParams: parsed };
      } catch {
        return { ...base, additionalParams: {} };
      }
    }
    return base;
  };

  const handleSubmit = async () => {
    const isValid = validateAllFields();
    if (!isValid) return;
    if (!form.supplier) {
      setErrors((prev) => ({ ...prev, supplier: "Supplier is required" }));
      return;
    }
    try {
      const payload = buildApiPayload();
      await createCredential.execute(payload);
      handleClose();
      onSubmit?.();
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || "Failed to create credential";
      setErrors((prev) => ({ ...prev, _submit: msg }));
    }
  };

  const renderAuthFields = () => {
    switch (form.authType) {
      case "API Key":
        return (
          <>
            <div className={styles.field}>
              <label>API KEY <span className={styles.required}>*</span></label>
              <div className={styles.passwordField}>
                <input
                  type={showApiKey ? "text" : "password"}
                  value={form.apiKey}
                  onChange={e => handleChange("apiKey", e.target.value)}
                  onBlur={() => handleBlur("apiKey")}
                  placeholder="Enter API key"
                  className={errors.apiKey ? styles.inputError : ""}
                />
                <button
                  type="button"
                  className={styles.eyeBtn}
                  onClick={() => setShowApiKey(!showApiKey)}
                >
                  {showApiKey ? "🙈" : "👁️"}
                </button>
              </div>
              {errors.apiKey && (
                <span className={styles.errorText}>{errors.apiKey}</span>
              )}
            </div>
            <div className={styles.field}>
              <label>API SECRET <span className={styles.required}>*</span></label>
              <div className={styles.passwordField}>
                <input
                  type={showApiSecret ? "text" : "password"}
                  value={form.apiSecret}
                  onChange={e => handleChange("apiSecret", e.target.value)}
                  onBlur={() => handleBlur("apiSecret")}
                  placeholder="Enter API secret"
                  className={errors.apiSecret ? styles.inputError : ""}
                />
                <button
                  type="button"
                  className={styles.eyeBtn}
                  onClick={() => setShowApiSecret(!showApiSecret)}
                >
                  {showApiSecret ? "🙈" : "👁️"}
                </button>
              </div>
              {errors.apiSecret && (
                <span className={styles.errorText}>{errors.apiSecret}</span>
              )}
            </div>
          </>
        );

      case "Basic Auth":
        return (
          <>
            <div className={styles.field}>
              <label>USERNAME <span className={styles.required}>*</span></label>
              <input
                type="text"
                value={form.username}
                onChange={e => handleChange("username", e.target.value)}
                onBlur={() => handleBlur("username")}
                placeholder="Enter username"
                className={errors.username ? styles.inputError : ""}
              />
              {errors.username && (
                <span className={styles.errorText}>{errors.username}</span>
              )}
            </div>
            <div className={styles.field}>
              <label>PASSWORD <span className={styles.required}>*</span></label>
              <div className={styles.passwordField}>
                <input
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={e => handleChange("password", e.target.value)}
                  onBlur={() => handleBlur("password")}
                  placeholder="Enter password"
                  className={errors.password ? styles.inputError : ""}
                />
                <button
                  type="button"
                  className={styles.eyeBtn}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? "🙈" : "👁️"}
                </button>
              </div>
              {errors.password && (
                <span className={styles.errorText}>{errors.password}</span>
              )}
            </div>
          </>
        );

      case "OAuth2":
        return (
          <>
            <div className={styles.field}>
              <label>CLIENT ID <span className={styles.required}>*</span></label>
              <input
                type="text"
                value={form.clientId}
                onChange={e => handleChange("clientId", e.target.value)}
                onBlur={() => handleBlur("clientId")}
                placeholder="Enter client ID"
                className={errors.clientId ? styles.inputError : ""}
              />
              {errors.clientId && (
                <span className={styles.errorText}>{errors.clientId}</span>
              )}
            </div>
            <div className={styles.field}>
              <label>CLIENT SECRET <span className={styles.required}>*</span></label>
              <div className={styles.passwordField}>
                <input
                  type={showClientSecret ? "text" : "password"}
                  value={form.clientSecret}
                  onChange={e => handleChange("clientSecret", e.target.value)}
                  onBlur={() => handleBlur("clientSecret")}
                  placeholder="Enter client secret"
                  className={errors.clientSecret ? styles.inputError : ""}
                />
                <button
                  type="button"
                  className={styles.eyeBtn}
                  onClick={() => setShowClientSecret(!showClientSecret)}
                >
                  {showClientSecret ? "🙈" : "👁️"}
                </button>
              </div>
              {errors.clientSecret && (
                <span className={styles.errorText}>{errors.clientSecret}</span>
              )}
            </div>
          </>
        );

      case "Bearer Token":
        return (
          <div className={styles.fieldWide}>
            <label>BEARER TOKEN <span className={styles.required}>*</span></label>
            <div className={styles.passwordField}>
              <input
                type={showBearerToken ? "text" : "password"}
                value={form.bearerToken}
                onChange={e => handleChange("bearerToken", e.target.value)}
                onBlur={() => handleBlur("bearerToken")}
                placeholder="Enter bearer token"
                className={errors.bearerToken ? styles.inputError : ""}
              />
              <button
                type="button"
                className={styles.eyeBtn}
                onClick={() => setShowBearerToken(!showBearerToken)}
              >
                {showBearerToken ? "🙈" : "👁️"}
              </button>
            </div>
            {errors.bearerToken && (
              <span className={styles.errorText}>{errors.bearerToken}</span>
            )}
          </div>
        );

      case "Custom":
        return (
          <div className={styles.fieldWide}>
            <label>CUSTOM AUTHENTICATION <span className={styles.required}>*</span></label>
            <textarea
              value={form.customAuth}
              onChange={e => handleChange("customAuth", e.target.value)}
              onBlur={() => handleBlur("customAuth")}
              placeholder='Enter custom authentication configuration (e.g., JSON)
Example:
{
  "type": "custom",
  "headers": {
    "X-API-Key": "your-key"
  }
}'
              rows={6}
              className={`${styles.textarea} ${errors.customAuth ? styles.inputError : ""}`}
            />
            {errors.customAuth && (
              <span className={styles.errorText}>{errors.customAuth}</span>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const hasErrors = Object.keys(errors).length > 0 && Object.values(errors).some(e => e !== "");
  const submitError = errors._submit;

  return (
    <BaseModal
      open={open}
      onClose={handleClose}
      title="Add Credential Set"
      width="720px"
      footer={
        <>
          <button className={styles.closeBtn} onClick={handleClose}>
            ✕ Close
          </button>
          <button className={styles.saveBtn} onClick={handleSubmit} disabled={createCredential.loading}>
            {createCredential.loading ? "Saving..." : "🔒 Save Changes"}
          </button>
        </>
      }
    >
      {/* Icon Header */}
      <div className={styles.iconHeader}>
        <div className={styles.icon}>🔗</div>
        <div className={styles.headerText}>
          <h3>API Credential Details</h3>
          <p>View and manage API authentication configuration</p>
        </div>
      </div>

      {/* Submit Error */}
      {submitError && (
        <div className={styles.errorBanner}>
          <div className={styles.errorIcon}>⚠️</div>
          <div className={styles.errorContent}>
            <span>{submitError}</span>
          </div>
        </div>
      )}

      {/* Validation Error Banner */}
      {hasErrors && !submitError && (
        <div className={styles.errorBanner}>
          <div className={styles.errorIcon}>⚠️</div>
          <div className={styles.errorContent}>
            <strong>Please fix the following errors:</strong>
            <ul>
              {Object.entries(errors).map(([key, error]) => 
                error && <li key={key}>{error}</li>
              )}
            </ul>
          </div>
        </div>
      )}

      {/* Basic Information Section */}
      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>BASIC INFORMATION</h4>
        <div className={styles.formGrid}>
          <div className={styles.field}>
            <label>CREDENTIAL SET ID <span className={styles.required}>*</span></label>
            <input
              value={form.credentialId}
              onChange={e =>
                handleChange("credentialId", e.target.value.toUpperCase())
              }
              onBlur={() => handleBlur("credentialId")}
              placeholder="CRED-001"
              className={`${styles.credIdInput} ${errors.credentialId ? styles.inputError : ""}`}
            />
            {errors.credentialId && (
              <span className={styles.errorText}>{errors.credentialId}</span>
            )}
          </div>
          <div className={styles.field}>
            <label>SUPPLIER <span className={styles.required}>*</span></label>
            <select
              value={form.supplier}
              onChange={e => handleChange("supplier", e.target.value)}
              onBlur={() => handleBlur("supplier")}
              className={errors.supplier ? styles.inputError : ""}
            >
              <option value="">Select supplier</option>
              {supplierOptions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            {errors.supplier && (
              <span className={styles.errorText}>{errors.supplier}</span>
            )}
          </div>
          <div className={styles.field}>
            <label>ENVIRONMENT <span className={styles.required}>*</span></label>
            <select
              value={form.environment}
              onChange={e => handleChange("environment", e.target.value)}
              onBlur={() => handleBlur("environment")}
              className={errors.environment ? styles.inputError : ""}
            >
              <option value="">Select environment</option>
              {ENVIRONMENTS.map(env => (
                <option key={env} value={env}>
                  {env}
                </option>
              ))}
            </select>
            {errors.environment && (
              <span className={styles.errorText}>{errors.environment}</span>
            )}
          </div>
          <div className={styles.field}>
            <label>STATUS</label>
            <select
              value={form.status}
              onChange={e => handleChange("status", e.target.value)}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Base URL Section */}
      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>🌐 BASE URL</h4>
        <div className={styles.fieldWide}>
          <label>API ENDPOINT <span className={styles.required}>*</span></label>
          <input
            value={form.apiEndpoint}
            onChange={e => handleChange("apiEndpoint", e.target.value)}
            onBlur={() => handleBlur("apiEndpoint")}
            placeholder="https://api.amadeusgds.com/v2"
            className={errors.apiEndpoint ? styles.inputError : ""}
          />
          {errors.apiEndpoint && (
            <span className={styles.errorText}>{errors.apiEndpoint}</span>
          )}
        </div>
      </div>

      {/* Authentication Configuration Section */}
      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>🔒 AUTHENTICATION CONFIGURATION</h4>
        <div className={styles.fieldWide}>
          <label>AUTHENTICATION TYPE <span className={styles.required}>*</span></label>
          <select
            value={form.authType}
            onChange={e => handleChange("authType", e.target.value)}
            onBlur={() => handleBlur("authType")}
            className={errors.authType ? styles.inputError : ""}
          >
            <option value="">Select authentication type</option>
            {AUTH_TYPES.map(type => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
          {errors.authType && (
            <span className={styles.errorText}>{errors.authType}</span>
          )}
        </div>

        {form.authType && (
          <div className={styles.formGrid}>{renderAuthFields()}</div>
        )}
      </div>

      {/* Security Notice */}
      <div className={styles.securityNotice}>
        <div className={styles.noticeIcon}>🛡️</div>
        <div className={styles.noticeContent}>
          <strong>Security Notice</strong>
          <p>
            All credential access is logged. Never share credentials outside
            authorized systems.
          </p>
        </div>
      </div>
    </BaseModal>
  );
};

export default AddCredentialSetModal;